import React from "react";
import { LazyQueryHookOptions } from "@apollo/client";
import { IndexForms } from "@hex-insights/app-modules";
import { ClassNameProps, StyleProps } from "@hex-insights/core";
import { FieldDisplayArgs, FormValues, RadioField, RadioFieldProps } from "@hex-insights/forms";
import {
	CourseAssessmentCategory,
	CourseAssessmentCategorySelectQuery,
	CourseAssessmentCategorySelectQueryVariables,
	useCourseAssessmentCategorySelectLazyQuery,
} from "../../../Utilities";
import { CourseAssessmentCategorySelect } from "../../FormUtilities";
import { CourseAssessmentCategoryLink } from "../../Links";
import { CourseAssessmentCategoryOrderFormValues } from "../../Order";

export type CourseAssessmentCategoryRadioProps<FV extends FormValues> = Omit<
	RadioFieldProps<FV, CourseAssessmentCategory["id"] | null>,
	"options" | "isLoading" | "blankValue" | "className" | "style"
> & {
	currentInstance?: CourseAssessmentCategorySelect.ModelForOption | null;
	queryOptions?: LazyQueryHookOptions<
		CourseAssessmentCategorySelectQuery,
		CourseAssessmentCategorySelectQueryVariables
	>;
	load?: boolean;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Select field for the CourseAssessmentCategory model.
 */
export function CourseAssessmentCategoryRadio<FV extends FormValues>({
	formState,
	name,
	currentInstance,
	display = displayCourseAssessmentCategory,
	queryOptions,
	load,
	...props
}: CourseAssessmentCategoryRadioProps<FV>) {
	const defaultOrder = IndexForms.useOrderInput(CourseAssessmentCategoryOrderFormValues.initial);
	const [loadOptions, { loading, data, refetch }] = useCourseAssessmentCategorySelectLazyQuery({
		...queryOptions,
		variables: {
			...queryOptions?.variables,
			order: queryOptions?.variables?.order ?? defaultOrder,
		},
		notifyOnNetworkStatusChange: true,
	});
	const isEditing = formState.formEditing[name];
	React.useEffect(() => {
		if (load || (load !== false && isEditing)) {
			loadOptions();
		}
	}, [load, isEditing, loadOptions]);
	const options = React.useMemo(
		() => CourseAssessmentCategorySelect.toOptions(data?.courseAssessmentCategoryConnection.edges, currentInstance),
		[data, currentInstance],
	);
	const refetchOptions = React.useCallback(() => refetch(), [refetch]);

	return (
		<RadioField
			formState={formState}
			name={name}
			isLoading={loading}
			options={options}
			display={display}
			blankValue={null}
			refetchOptions={refetchOptions}
			{...props}
		/>
	);
}

function displayCourseAssessmentCategory({ value: id, formattedValue }: FieldDisplayArgs<string | null>) {
	if (id === null) {
		return formattedValue;
	}
	return <CourseAssessmentCategoryLink instance={{ id }}>{formattedValue}</CourseAssessmentCategoryLink>;
}
